/**
 * PRODUCT CATALOGUE
 *
 * Organizer-level list of non-ticket items — drinks, merchandise, games. Created once here
 * and reusable in two independent ways:
 *   1. Sold standalone at a venue via a printed QR code (Stripe Checkout, no event)
 *   2. Selected into an event as an add-on (existing ticket-shop flow, own online price)
 *
 * Rendered as a tab panel inside ProductsPage — it does not own the page chrome, so the
 * "Add product" action sits in an inline toolbar rather than the layout header.
 *
 * See ONSITE_PRODUCTS_DESIGN.md.
 */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@heroicons/react/20/solid";

import noEventImage from "../../assets/noEventImage.svg";
import { TwButton, TwConfirmDialog } from "../../components/tailwind";
import WebShopCard from "./WebShopCard";
import {
  clearLoadingData,
  loadingState,
  setSectionLoading,
} from "../../redux/reducers/loadingReducer";
import { listProducts, archiveProduct } from "../../utils/productsApi";

import AddEditProductDrawer from "./AddEditProductDrawer";
import ProductQrModal, { generateAndDownloadQr } from "./ProductQrModal";
import ProductTile from "./ProductTile";

export default function ProductsList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loadingReduxState = useSelector(loadingState);

  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState("");

  const [editOpen, setEditOpen] = React.useState(false);
  const [isEdit, setIsEdit] = React.useState(false);
  const [selected, setSelected] = React.useState({});

  const [qrOpen, setQrOpen] = React.useState(false);
  const [qrProduct, setQrProduct] = React.useState({});

  const [archiveTarget, setArchiveTarget] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    dispatch(setSectionLoading(true));

    const result = await listProducts();
    if (result.error) {
      setLoadError(result.message);
    } else {
      setLoadError("");
      setProducts(Array.isArray(result.data) ? result.data : []);
    }

    dispatch(clearLoadingData());
    setLoading(false);
  }, [dispatch]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleArchive = async () => {
    if (!archiveTarget?._id) return;
    await archiveProduct(archiveTarget._id);
    setArchiveTarget(null);
    load();
  };

  const isEmpty =
    products.length === 0 &&
    loading === false &&
    loadingReduxState.sectionLoading === false;

  return (
    <div>
      {/* One link and one QR for the whole catalogue, above the per-product codes. */}
      <WebShopCard />

      <div className="flex flex-wrap justify-end gap-3 mb-5">
        <TwButton
          className="sm:min-w-32"
          onClick={() => {
            setSelected({});
            setIsEdit(false);
            setEditOpen(true);
          }}
        >
          <PlusIcon className="size-5" />
          &nbsp;&nbsp;{t("products.addProduct", "Add product")}
        </TwButton>
      </div>

          <p className="text-sm text-gray-500 mb-5 max-w-2xl">
            {t(
              "products.intro",
              "Items you sell at your events that aren't tickets. Print a product's QR code to sell it on the spot, or add it to an event as an add-on. Prices include VAT."
            )}
          </p>

          {loadError ? (
            <p className="text-sm text-red-600 mb-4">{loadError}</p>
          ) : null}

          {products.length ? (
            <ul className="space-y-3 sm:max-w-3xl">
              {products.map((product) => (
                <ProductTile
                  key={product._id}
                  product={product}
                  onEdit={() => {
                    setSelected(product);
                    setIsEdit(true);
                    setEditOpen(true);
                  }}
                  onShowQr={() => {
                    setQrProduct(product);
                    setQrOpen(true);
                  }}
                  onDownloadQr={() => generateAndDownloadQr(product)}
                  onArchive={() => setArchiveTarget(product)}
                />
              ))}
            </ul>
          ) : isEmpty ? (
            <div className="flex flex-col justify-center items-center gap-5 text-center h-full">
              <img className="w-40 md:w-60" src={noEventImage} alt="" />
              <div className="text-secondary text-lg font-bold mt-3">
                {t("products.noProducts", "No products yet")}
              </div>
              <p className="text-sm text-gray-500 max-w-sm">
                {t(
                  "products.noProductsHelper",
                  "Add a product once and reuse it at every event — print its QR code to sell on-site, or select it as an event add-on."
                )}
              </p>
            </div>
          ) : null}

          <AddEditProductDrawer
            open={editOpen}
            isEdit={isEdit}
            product={selected}
            handleClose={() => setEditOpen(false)}
            handleUpdate={() => {
              setEditOpen(false);
              load();
            }}
          />

          <ProductQrModal
            open={qrOpen}
            product={qrProduct}
            handleClose={() => setQrOpen(false)}
          />

          <TwConfirmDialog
            dialogOpen={!!archiveTarget}
            dialogTitle={t("products.archiveTitle", "Archive product?")}
            dialogMessage={t(
              "products.archiveMessage",
              "This hides the product and stops its QR code from taking payments. Any codes already printed will show it as unavailable rather than breaking. Past sales are unaffected."
            )}
            dialogType="warning"
            btnConfirmText={t("products.archiveConfirm", "Archive")}
            btnCloseText={t("common.cancel", "Cancel")}
            onConfirm={handleArchive}
            onClose={() => setArchiveTarget(null)}
          />
    </div>
  );
}
